import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import AdminLayout from '../AdminLayout';
import { useGetCasesQuery } from 'store/services/case/caseApi';
import { CaseStatus } from 'types/interfaces/case';
import { getAvatarPlaceholder } from 'utils/avatarUtils';
import './CasesPage.css';

const CasesPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'unresolved' | 'resolved' | 'all'>('unresolved');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<'caseDate' | 'tradeDate'>('caseDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to page 1 on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, error } = useGetCasesQuery({
    page,
    pageSize: 100,
    status: statusFilter,
    search: debouncedSearch,
    sortBy,
    sortOrder
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadgeClass = (status: CaseStatus) => {
    switch (status) {
      case CaseStatus.UNREAD:
        return 'status-unread';
      case CaseStatus.READ:
        return 'status-read';
      case CaseStatus.IN_PROGRESS:
        return 'status-in-progress';
      case CaseStatus.RESOLVED:
        return 'status-resolved';
      default:
        return '';
    }
  };

  const getStatusLabel = (status: CaseStatus) => {
    switch (status) {
      case CaseStatus.UNREAD:
        return 'Unread';
      case CaseStatus.READ:
        return 'Read';
      case CaseStatus.IN_PROGRESS:
        return 'In Progress';
      case CaseStatus.RESOLVED:
        return 'Resolved';
      default:
        return status;
    }
  };

  const handleRowClick = (caseId: string) => {
    navigate(`/admin/cases/${caseId}`);
  };

  const handleSort = (column: 'caseDate' | 'tradeDate') => {
    if (sortBy === column) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (column: 'caseDate' | 'tradeDate') => {
    if (sortBy !== column) return '';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  if (isLoading) {
    return (
      <AdminLayout title="Case Resolution" currentPath="/admin/cases">
        <div className="cases-page">
          <div className="cases-loading">Loading cases...</div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Case Resolution" currentPath="/admin/cases">
        <div className="cases-page">
          <div className="cases-error">Error loading cases. Please try again.</div>
        </div>
      </AdminLayout>
    );
  }

  const cases = data?.cases || [];

  return (
    <AdminLayout title="Case Resolution" currentPath="/admin/cases">
      <div className="cases-page">
        <div className="cases-header">
          <h1 className="cases-title">Case Resolution</h1>

          <div className="cases-controls">
            <input
              type="text"
              className="cases-search-input"
              placeholder="Search by username or trade number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="cases-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'unresolved' | 'resolved' | 'all')}
            >
              <option value="unresolved">Unresolved</option>
              <option value="resolved">Resolved</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        <div className="cases-table-container">
          <table className="cases-table">
            <thead>
              <tr>
                <th>Sender</th>
                <th>Trade Number</th>
                <th>Receiver</th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort('tradeDate')}
                >
                  Trade Date{getSortIcon('tradeDate')}
                </th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort('caseDate')}
                >
                  Case Date{getSortIcon('caseDate')}
                </th>
                <th>Due Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {cases.length === 0 ? (
                <tr>
                  <td colSpan={7} className="cases-empty">
                    {debouncedSearch
                      ? `No cases found for "${debouncedSearch}"`
                      : 'No cases yet. When users summon an admin, they\'ll appear here.'}
                  </td>
                </tr>
              ) : (
                cases.map((caseItem) => {
                  // Get avatars or placeholders for sender
                  const senderPlaceholder = getAvatarPlaceholder(caseItem.senderUsername);
                  const receiverPlaceholder = getAvatarPlaceholder(caseItem.receiverUsername);

                  return (
                    <tr
                      key={caseItem.id}
                      onClick={() => handleRowClick(caseItem.id)}
                      className={`cases-row ${caseItem.status === CaseStatus.UNREAD ? 'unread' : ''}`}
                    >
                      <td>
                        <div className="cases-user">
                          {caseItem.trade?.sender?.avatarUrl ? (
                            <img
                              src={caseItem.trade.sender.avatarUrl}
                              alt={caseItem.senderUsername}
                              className="cases-avatar"
                            />
                          ) : senderPlaceholder ? (
                            <div
                              className="cases-avatar cases-avatar-placeholder"
                              style={{ backgroundColor: senderPlaceholder.color }}
                            >
                              <span>{senderPlaceholder.letter}</span>
                            </div>
                          ) : (
                            <div className="cases-avatar cases-avatar-placeholder">
                              <span>?</span>
                            </div>
                          )}
                          <span className="cases-username">{caseItem.senderUsername}</span>
                        </div>
                      </td>
                      <td>
                        <span className="trade-number">{caseItem.tradeNumber}</span>
                      </td>
                      <td>
                        <div className="cases-user">
                          {caseItem.trade?.receiver?.avatarUrl ? (
                            <img
                              src={caseItem.trade.receiver.avatarUrl}
                              alt={caseItem.receiverUsername}
                              className="cases-avatar"
                            />
                          ) : receiverPlaceholder ? (
                            <div
                              className="cases-avatar cases-avatar-placeholder"
                              style={{ backgroundColor: receiverPlaceholder.color }}
                            >
                              <span>{receiverPlaceholder.letter}</span>
                            </div>
                          ) : (
                            <div className="cases-avatar cases-avatar-placeholder">
                              <span>?</span>
                            </div>
                          )}
                          <span className="cases-username">{caseItem.receiverUsername}</span>
                        </div>
                      </td>
                      <td className="cases-date">
                        {caseItem.trade?.createdAt ? formatDate(caseItem.trade.createdAt) : '-'}
                      </td>
                      <td className="cases-date">{formatDate(caseItem.createdAt)}</td>
                      <td className="cases-date">
                        {caseItem.dueDate ? formatDate(caseItem.dueDate) : '-'}
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusBadgeClass(caseItem.status)}`}>
                          {getStatusLabel(caseItem.status)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {data?.pagination && data.pagination.totalPages > 0 && (
          <div className="cases-pagination">
            <button
              className="pagination-btn"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {data.pagination.currentPage} of {data.pagination.totalPages}
              {' '}({data.pagination.totalCases} total cases)
            </span>
            <button
              className="pagination-btn"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= data.pagination.totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CasesPage;
